import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../../services/api';
import RecommendationExplanation from '../common/RecommendationExplanation';
import ConfirmationModal from '../ConfirmationModal';
import './WeeklyMealPlanGenerator.css';

const CATEGORY_ORDER = ['PRODUCE', 'DAIRY', 'MEAT', 'GRAINS', 'PANTRY', 'FROZEN', 'BEVERAGES', 'SNACKS', 'OTHER'];
const CATEGORY_ICONS = {
  PRODUCE: '🥬', DAIRY: '🥛', MEAT: '🥩', GRAINS: '🌾', PANTRY: '🥫',
  FROZEN: '🧊', BEVERAGES: '🥤', SNACKS: '🍿', OTHER: '📦'
};

function WeeklyMealPlanGenerator({ userId, profile, userPreferences, symptoms, onMealPlanGenerated, onMealSelect, onNavigateToGrocery }) {
  const [filters, setFilters] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [groceryList, setGroceryList] = useState([]);
  const [groupedGroceries, setGroupedGroceries] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(new Set(['PRODUCE', 'DAIRY'])); // Default expand first 2
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [showMealActionModal, setShowMealActionModal] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(120); // Default fallback
  
  // Calculate header height dynamically
  useEffect(() => {
    const calculateHeaderHeight = () => {
      const headerWrapper = document.querySelector('.navigation-header-wrapper');
      if (headerWrapper) {
        const height = headerWrapper.offsetHeight;
        setHeaderHeight(height + 20); // Add 20px for extra padding
      }
    };
    
    calculateHeaderHeight();
    window.addEventListener('resize', calculateHeaderHeight);
    // Also check after a delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateHeaderHeight, 100);
    const timeoutId2 = setTimeout(calculateHeaderHeight, 500);
    
    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, []);
  
  const filterOptions = [
    { id: 'quick', label: 'Quick Meals', icon: '⚡' },
    { id: 'low_nausea', label: 'Low Nausea', icon: '💚' },
    { id: 'high_protein', label: 'High Protein', icon: '💪' },
    { id: 'iron_booster', label: 'Iron Booster', icon: '🔴' },
    { id: 'budget_friendly', label: 'Budget Friendly', icon: '💰' }
  ];
  
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  const handleGenerate = async () => {
    if (!userId) {
      setFeedbackModal({
        isOpen: true,
        title: 'User ID required',
        message: 'User ID is required to generate a meal plan',
        type: 'error'
      });
      return;
    }
    
    setGenerating(true);
    setGroceryList([]);
    setGroupedGroceries({});
    try {
      console.log('📅 Generating weekly plan with filters:', filters);
      console.log('   userId:', userId);
      
      const response = await api.generateWeeklyPlan(userId, filters);
      console.log('📥 Weekly plan response:', response);
      
      // apiClient already extracts data, so response is the data directly
      const plan = Array.isArray(response) ? { days: response } : (response?.data || response || {});
      console.log('📋 Processed plan:', plan);
      
      // Convert date strings to Date objects and ensure all meals exist
      // Weekly plan should start from today and go for 7 days
      if (plan.days) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset to start of day
        
        plan.days = plan.days.map((day, index) => {
          let dateObj;
          if (day.date) {
            // Handle both string and Date objects
            dateObj = typeof day.date === 'string' ? new Date(day.date) : new Date(day.date);
            // Ensure it's a valid date
            if (isNaN(dateObj.getTime())) {
              // Invalid date, use today + index
              dateObj = new Date(today);
              dateObj.setDate(dateObj.getDate() + index);
            } else {
              // Valid date, but ensure it's normalized to start of day
              dateObj.setHours(0, 0, 0, 0);
            }
          } else {
            // Default to today + index days
            dateObj = new Date(today);
            dateObj.setDate(dateObj.getDate() + index);
          }
          
          return {
            ...day,
            date: dateObj,
            breakfast: day.breakfast || { name: 'Not assigned', id: null },
            lunch: day.lunch || { name: 'Not assigned', id: null },
            dinner: day.dinner || { name: 'Not assigned', id: null },
            snack: day.snack || { name: 'Not assigned', id: null }
          };
        });
        
        // Ensure we have exactly 7 days starting from today
        if (plan.days.length < 7) {
          const existingDates = plan.days.map(d => d.date.getTime());
          for (let i = plan.days.length; i < 7; i++) {
            const newDate = new Date(today);
            newDate.setDate(newDate.getDate() + i);
            if (!existingDates.includes(newDate.getTime())) {
              plan.days.push({
                date: newDate,
                breakfast: { name: 'Not assigned', id: null },
                lunch: { name: 'Not assigned', id: null },
                dinner: { name: 'Not assigned', id: null },
                snack: { name: 'Not assigned', id: null }
              });
            }
          }
        }
        
        // Sort days by date to ensure chronological order
        plan.days.sort((a, b) => a.date.getTime() - b.date.getTime());
      }
      
      if (!plan.days || plan.days.length === 0) {
        console.warn('⚠️ No days in plan response');
        setFeedbackModal({
          isOpen: true,
          title: 'No meal plan generated',
          message: 'No meal plan was generated. Please try again or check your profile settings.',
          type: 'error'
        });
        return;
      }
      
      console.log('✅ Successfully generated plan with', plan.days.length, 'days');
      setWeeklyPlan(plan);
      onMealPlanGenerated?.(plan);
      api.trackFeatureUse('weekly_plan_generated');

      // Auto-generate grocery list immediately (no separate button needed)
      try {
        const daysForApi = plan.days.map(d => {
          const apiDay = { ...d };
          if (apiDay.date instanceof Date) {
            apiDay.date = apiDay.date.toISOString().split('T')[0];
          }
          return apiDay;
        });
        const groceryRes = await api.generateGroceryList(daysForApi);
        const groceryData = groceryRes?.data ?? groceryRes;
        const items = groceryData?.all ?? groceryData?.items ?? (Array.isArray(groceryData) ? groceryData : []);
        if (items.length > 0) {
          const grouped = items.reduce((acc, item) => {
            const cat = (item.category || 'OTHER').toUpperCase();
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(item);
            return acc;
          }, {});
          setGroceryList(items);
          setGroupedGroceries(grouped);
          setExpandedCategories(new Set(CATEGORY_ORDER.filter(c => grouped[c]?.length)));
        } else {
          setGroceryList([]);
          setGroupedGroceries({});
        }
      } catch (gErr) {
        console.warn('Grocery list generation failed:', gErr);
        setGroceryList([]);
        setGroupedGroceries({});
      }
    } catch (error) {
      console.error('❌ Error generating weekly plan:', error);
      console.error('   Error details:', error.response?.data || error.message);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to generate weekly plan. Please try again.';
      setFeedbackModal({
        isOpen: true,
        title: 'Generation failed',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };
  
  const toggleFilter = (filterId) => {
    setFilters(prev => 
      prev.includes(filterId) 
        ? prev.filter(f => f !== filterId)
        : [...prev, filterId]
    );
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const expandAllCategories = () => {
    setExpandedCategories(new Set(Object.keys(groupedGroceries)));
  };

  const collapseAllCategories = () => {
    setExpandedCategories(new Set());
  };
  
  const handleMealClick = (meal, mealType, dayDate) => {
    if (!meal || !meal.name || meal.name === 'Not assigned') return;
    
    console.log('🔍 Meal clicked:', meal.name);
    console.log('🔍 Meal data:', meal);
    console.log('🔍 Has whyRecommended?', meal.whyRecommended ? 'YES' : 'NO');
    if (meal.whyRecommended) {
      console.log('📝 Explanation:', meal.whyRecommended.substring(0, 200) + '...');
    }
    
    setSelectedMeal({ meal, mealType, dayDate });
    setShowMealActionModal(true);
  };
  
  const handleAddToDailyPlan = (targetMealType) => {
    if (!selectedMeal || !onMealSelect) {
      console.warn('⚠️ Cannot add meal - missing selectedMeal or onMealSelect');
      return;
    }
    
    console.log('➕ Adding meal to daily plan:', { 
      meal: selectedMeal.meal.name, 
      targetMealType,
      originalDate: selectedMeal.dayDate 
    });
    
    // Add the meal to today's daily plan
    onMealSelect(targetMealType, selectedMeal.meal);
    
    // Close modal and reset
    setShowMealActionModal(false);
    setSelectedMeal(null);
  };
  
  return (
    <div className="weekly-meal-plan-generator">
      <div className="generator-header">
        <h2>Generate Your Weekly Meal Plan</h2>
        <p>We'll create a personalized 7-day meal plan based on your preferences</p>
      </div>
      
      <div className="filters-section">
        <h3>Filter Options</h3>
        <div className="filters-grid">
          {filterOptions.map((filter) => (
            <motion.button
              key={filter.id}
              className={`filter-chip ${filters.includes(filter.id) ? 'active' : ''}`}
              onClick={() => toggleFilter(filter.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>{filter.icon}</span>
              <span>{filter.label}</span>
            </motion.button>
          ))}
        </div>
      </div>
      
      <motion.button
        className="generate-button"
        onClick={handleGenerate}
        disabled={generating}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {generating ? '✨ Generating...' : '✨ Generate Week Plan'}
      </motion.button>
      
      {weeklyPlan && weeklyPlan.days && weeklyPlan.days.length > 0 ? (() => {
        // Calculate current week for recommendation explanation
        let currentWeek = null;
        if (profile?.currentWeek) {
          currentWeek = profile.currentWeek;
        } else if (profile?.lmpDate) {
          const lmpDate = new Date(profile.lmpDate);
          const today = new Date();
          const diffTime = today - lmpDate;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          currentWeek = Math.floor(diffDays / 7) + 1;
        }
        
        return (
          <motion.div
            className="weekly-plan-display"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="plan-header">
              <h3>Your Weekly Plan</h3>
              <motion.button
                className="regenerate-button"
                onClick={handleGenerate}
                disabled={generating}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🔄 Regenerate
              </motion.button>
            </div>
            
            {/* Beautiful explanation of why these recommendations were made */}
            {weeklyPlan.healthProfile && (
              <RecommendationExplanation
                healthProfile={weeklyPlan.healthProfile}
                userPreferences={userPreferences}
                pregnancyProfile={profile}
                currentWeek={currentWeek}
                recommendationType="meal"
              />
            )}
          
          <div className="week-grid">
            {weeklyPlan.days.map((day, idx) => {
              const dateObj = day.date instanceof Date ? day.date : new Date(day.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const dayDate = new Date(dateObj);
              dayDate.setHours(0, 0, 0, 0);
              const isToday = dayDate.getTime() === today.getTime();
              const isPast = dayDate.getTime() < today.getTime();
              
              // Format date with "Today" indicator
              let dateLabel;
              if (isToday) {
                dateLabel = `Today (${dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
              } else if (isPast) {
                dateLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              } else {
                dateLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              }
              
              return (
                <motion.div
                  key={idx}
                  className={`day-card ${isToday ? 'today' : ''} ${isPast ? 'past' : ''}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <h4>{dateLabel}</h4>
                  <div className="day-meals">
                    {day.breakfast && day.breakfast.name && day.breakfast.name !== 'Not assigned' ? (
                      <motion.div 
                        className="meal-item clickable"
                        onClick={() => handleMealClick(day.breakfast, 'breakfast', dateObj)}
                        whileHover={{ scale: 1.02, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="meal-icon">🌅</span>
                        <span className="meal-name">{day.breakfast.name}</span>
                        <span className="add-hint">+</span>
                      </motion.div>
                    ) : (
                      <div className="meal-item empty">
                        <span>🌅</span>
                        <span>Not assigned</span>
                      </div>
                    )}
                    {day.lunch && day.lunch.name && day.lunch.name !== 'Not assigned' ? (
                      <motion.div 
                        className="meal-item clickable"
                        onClick={() => handleMealClick(day.lunch, 'lunch', dateObj)}
                        whileHover={{ scale: 1.02, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="meal-icon">☀️</span>
                        <span className="meal-name">{day.lunch.name}</span>
                        <span className="add-hint">+</span>
                      </motion.div>
                    ) : (
                      <div className="meal-item empty">
                        <span>☀️</span>
                        <span>Not assigned</span>
                      </div>
                    )}
                    {day.dinner && day.dinner.name && day.dinner.name !== 'Not assigned' ? (
                      <motion.div 
                        className="meal-item clickable"
                        onClick={() => handleMealClick(day.dinner, 'dinner', dateObj)}
                        whileHover={{ scale: 1.02, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="meal-icon">🌙</span>
                        <span className="meal-name">{day.dinner.name}</span>
                        <span className="add-hint">+</span>
                      </motion.div>
                    ) : (
                      <div className="meal-item empty">
                        <span>🌙</span>
                        <span>Not assigned</span>
                      </div>
                    )}
                    {day.snack && day.snack.name && day.snack.name !== 'Not assigned' ? (
                      <motion.div 
                        className="meal-item clickable"
                        onClick={() => handleMealClick(day.snack, 'snack', dateObj)}
                        whileHover={{ scale: 1.02, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="meal-icon">🍎</span>
                        <span className="meal-name">{day.snack.name}</span>
                        <span className="add-hint">+</span>
                      </motion.div>
                    ) : (
                      <div className="meal-item empty">
                        <span>🍎</span>
                        <span>Not assigned</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

            {/* Grocery list - auto-generated, collapsible by category */}
            {groceryList.length > 0 && (
              <motion.div
                className="weekly-groceries-section"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="groceries-section-header">
                  <h3 className="groceries-title">🛒 Your Grocery List</h3>
                  <span className="groceries-count">{groceryList.length} items</span>
                  {onNavigateToGrocery && (
                    <button type="button" className="view-full-list-btn" onClick={() => onNavigateToGrocery('grocery')}>
                      View full list →
                    </button>
                  )}
                  <div className="groceries-expand-btns">
                    <button type="button" className="expand-collapse-btn" onClick={expandAllCategories}>Expand all</button>
                    <span className="expand-sep">·</span>
                    <button type="button" className="expand-collapse-btn" onClick={collapseAllCategories}>Collapse all</button>
                  </div>
                </div>
                <div className="groceries-categories">
                  {CATEGORY_ORDER.filter(cat => groupedGroceries[cat]?.length).map(cat => {
                    const items = groupedGroceries[cat] || [];
                    const isExpanded = expandedCategories.has(cat);
                    return (
                      <div key={cat} className={`grocery-category-block ${isExpanded ? 'expanded' : ''}`}>
                        <button
                          type="button"
                          className="grocery-category-header"
                          onClick={() => toggleCategory(cat)}
                        >
                          <span className="cat-icon">{CATEGORY_ICONS[cat] || '📦'}</span>
                          <span className="cat-name">{cat}</span>
                          <span className="cat-count">({items.length})</span>
                          <span className="cat-chevron">{isExpanded ? '▼' : '▶'}</span>
                        </button>
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              className="grocery-category-items"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {items.map((item, idx) => (
                                <div key={`${cat}-${idx}`} className="grocery-item-row">
                                  <span className="grocery-item-name">{item.name}</span>
                                  {item.quantity && (
                                    <span className="grocery-item-qty">{item.quantity}</span>
                                  )}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </motion.div>
        );
      })() : weeklyPlan && (!weeklyPlan.days || weeklyPlan.days.length === 0) ? (
        <div className="empty-plan-message">
          <p>No meal plan generated yet. Click "Generate Week Plan" to create your personalized plan!</p>
        </div>
      ) : null}
      
      {/* Meal Action Modal */}
      {showMealActionModal && selectedMeal && createPortal(
        <AnimatePresence>
          <motion.div
            className="meal-action-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMealActionModal(false)}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99999, // Very high z-index
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '20px',
              paddingTop: `${headerHeight}px`, // Dynamic padding based on actual header height
              overflowY: 'auto'
            }}
          >
            <motion.div
              className="meal-action-modal"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{ 
                maxHeight: `calc(100vh - ${headerHeight + 40}px)`, // Account for header and padding
                marginTop: '20px' // Small top margin for spacing
              }}
            >
            <div className="meal-action-header">
              <h3>Add to Daily Plan</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setShowMealActionModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="meal-action-content">
              {/* Meal Preview Section */}
              <div className="selected-meal-preview">
                <div className="meal-preview-icon">
                  {selectedMeal.mealType === 'breakfast' && '🌅'}
                  {selectedMeal.mealType === 'lunch' && '☀️'}
                  {selectedMeal.mealType === 'dinner' && '🌙'}
                  {selectedMeal.mealType === 'snack' && '🍎'}
                </div>
                <div className="meal-preview-info">
                  <h4>{selectedMeal.meal.name}</h4>
                  {selectedMeal.meal.description && (
                    <p className="meal-preview-description">{selectedMeal.meal.description}</p>
                  )}
                </div>
              </div>
              
              {/* Meal Details Section */}
              <div className="meal-details-section">
                <div className="meal-details-grid">
                  {selectedMeal.meal.prepTime && (
                    <div className="detail-item">
                      <span className="detail-icon">⏱️</span>
                      <div className="detail-content">
                        <span className="detail-label">Prep Time</span>
                        <span className="detail-value">{selectedMeal.meal.prepTime}</span>
                      </div>
                    </div>
                  )}
                  {selectedMeal.meal.calories && (
                    <div className="detail-item">
                      <span className="detail-icon">🔥</span>
                      <div className="detail-content">
                        <span className="detail-label">Calories</span>
                        <span className="detail-value">{selectedMeal.meal.calories} cal</span>
                      </div>
                    </div>
                  )}
                  {selectedMeal.meal.difficulty && (
                    <div className="detail-item">
                      <span className="detail-icon">📊</span>
                      <div className="detail-content">
                        <span className="detail-label">Difficulty</span>
                        <span className="detail-value">{selectedMeal.meal.difficulty}</span>
                      </div>
                    </div>
                  )}
                  {selectedMeal.meal.source && (
                    <div className="detail-item">
                      <span className="detail-icon">📚</span>
                      <div className="detail-content">
                        <span className="detail-label">Source</span>
                        <span className="detail-value">{selectedMeal.meal.source}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {selectedMeal.meal.nutrients && selectedMeal.meal.nutrients.length > 0 && (
                  <div className="nutrients-section">
                    <h5>Key Nutrients</h5>
                    <div className="nutrients-list">
                      {selectedMeal.meal.nutrients.map((nutrient, idx) => (
                        <span key={idx} className="nutrient-badge">{nutrient}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedMeal.meal.ingredients && selectedMeal.meal.ingredients.length > 0 && (
                  <div className="ingredients-section">
                    <h5>Ingredients</h5>
                    <ul className="ingredients-list">
                      {selectedMeal.meal.ingredients.map((ingredient, idx) => (
                        <li key={idx}>{ingredient}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedMeal.meal.recipe && (
                  <div className="recipe-section">
                    <h5>How to Prepare</h5>
                    <div className="recipe-content">
                      {selectedMeal.meal.recipe.split('\n').map((step, idx) => (
                        <div key={idx} className="recipe-step">
                          <span className="step-number">{idx + 1}</span>
                          <span className="step-text">{step.trim()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Personalized Explanation - Why This Meal is Recommended */}
                {selectedMeal.meal.whyRecommended && (
                  <div className="meal-explanation-section" style={{ marginTop: '20px' }}>
                    <div className="explanation-header">
                      <span className="explanation-icon">✨</span>
                      <h5>Why This Meal is Perfect for You</h5>
                    </div>
                    <div className="explanation-content">
                      <div className="explanation-text">
                        {selectedMeal.meal.whyRecommended.split('\n').map((line, idx) => {
                          if (line.trim().startsWith('•')) {
                            return (
                              <div key={idx} className="explanation-point">
                                {line.trim().substring(1).trim()}
                              </div>
                            );
                          } else if (line.trim()) {
                            return (
                              <div key={idx} className="explanation-paragraph">
                                {line.trim()}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action Message */}
              <div className="meal-action-message">
                <div className="message-icon">✨</div>
                <div className="message-content">
                  <h5>Add to Today's Daily Plan</h5>
                  {selectedMeal.dayDate && (() => {
                    const mealDate = selectedMeal.dayDate instanceof Date ? selectedMeal.dayDate : new Date(selectedMeal.dayDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    mealDate.setHours(0, 0, 0, 0);
                    const isToday = mealDate.getTime() === today.getTime();
                    const dateStr = mealDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                    
                    return (
                      <p>
                        This meal was suggested for <strong className="original-meal-type">{selectedMeal.mealType.charAt(0).toUpperCase() + selectedMeal.mealType.slice(1)}</strong> on <strong>{isToday ? 'today' : dateStr}</strong>. 
                        {isToday ? (
                          <> You can add it to any meal slot in your daily plan. Select where you'd like to add it:</>
                        ) : (
                          <> You can add it to today's plan in any meal slot. Select where you'd like to add it:</>
                        )}
                      </p>
                    );
                  })()}
                  {!selectedMeal.dayDate && (
                    <p>This meal was originally suggested for <strong className="original-meal-type">{selectedMeal.mealType.charAt(0).toUpperCase() + selectedMeal.mealType.slice(1)}</strong>. You can add it to any meal slot in your daily plan. Select where you'd like to add it:</p>
                  )}
                </div>
              </div>
              
              {/* Meal Type Options */}
              <div className="meal-type-selection-section">
                <h5 className="selection-title">Select Meal Type:</h5>
                <div className="meal-type-options">
                  {['breakfast', 'lunch', 'dinner', 'snack'].map((mealType) => (
                    <motion.button
                      key={mealType}
                      className={`meal-type-option ${selectedMeal.mealType === mealType ? 'current' : ''}`}
                      onClick={() => handleAddToDailyPlan(mealType)}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="option-icon">
                        {mealType === 'breakfast' && '🌅'}
                        {mealType === 'lunch' && '☀️'}
                        {mealType === 'dinner' && '🌙'}
                        {mealType === 'snack' && '🍎'}
                      </span>
                      <span className="option-label">
                        {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                      </span>
                      {selectedMeal.mealType === mealType && (
                        <span className="current-badge">Originally Suggested</span>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>
              
              {/* Helpful Hint */}
              <div className="action-hint">
                <span className="hint-icon">💡</span>
                <span className="hint-text">After adding, you'll be automatically taken to the Daily Builder tab where you can see all your meals for today!</span>
              </div>
            </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      <ConfirmationModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ isOpen: false, title: '', message: '', type: 'error' })}
        onConfirm={() => {}}
        title={feedbackModal.title}
        message={feedbackModal.message}
        type={feedbackModal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default WeeklyMealPlanGenerator;

